// src/components/AiDashboard/AiMyNutritionStatus/nutrientCopyHelpers.js
// Shared helpers for building human-readable nutrient baseline sentences.

/**
 * Safely check whether a value is a finite number.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}

/**
 * Helper class for turning nutrient summary objects into human-friendly copy.
 */
export class NutrientCopyHelpers {
  /**
   * Internal numeric guard used by other helpers.
   *
   * @param {unknown} value
   * @returns {boolean}
   */
  static isFiniteNumber(value) {
    return isFiniteNumber(value);
  }

  /**
   * Build a short, neutral baseline sentence for a nutrient.
   *
   * Examples:
   *  - "Calcium was around 27% of your reference intake."
   *  - "Carbohydrates were around 26% of your reference intake."
   *
   * Notes:
   *  - It deliberately does NOT repeat "low", "high" or "on track" because
   *    that is covered in the recommendation/summary text.
   *  - Works with nutrient items shaped like entries from comparisonVsTargets,
   *    as long as you attach displayName/name/key and percentOfTarget.
   *
   * @param {object} nutrientItem
   * @param {string} [nutrientItem.displayName]
   * @param {string} [nutrientItem.name]
   * @param {string} [nutrientItem.key]
   * @param {number|null} [nutrientItem.percentOfTarget]
   * @param {boolean} [nutrientItem.isPlural]
   * @returns {string}
   */
  static buildNutrientBaselineSentence(nutrientItem) {
    if (!nutrientItem || typeof nutrientItem !== "object") {
      return "";
    }

    const rawLabel =
      nutrientItem.displayName ||
      nutrientItem.name ||
      nutrientItem.key ||
      "This nutrient";

    const label = String(rawLabel).trim() || "This nutrient";

    const percent = this.isFiniteNumber(nutrientItem.percentOfTarget)
      ? Math.round(nutrientItem.percentOfTarget)
      : null;

    const isPlural = Boolean(nutrientItem.isPlural);
    const verb = isPlural ? "were" : "was";

    if (percent !== null) {
      return `${label} ${verb} around ${percent}% of your reference intake.`;
    }

    return `${label} ${verb} compared with your reference intake.`;
  }
}

/**
 * Functional wrapper around NutrientCopyHelpers for convenience
 * and backwards-compatible imports:
 *
 *   import { buildNutrientBaselineSentence } from "./nutrientCopyHelpers";
 *
 * @param {object} nutrientItem
 * @returns {string}
 */
export function buildNutrientBaselineSentence(nutrientItem) {
  return NutrientCopyHelpers.buildNutrientBaselineSentence(nutrientItem);
}
